import * as React from "react";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagsEditor } from "@/shared/components/TagsEditor";
import { DatasetRecord } from "@/shared/domain/datasets";
import { useUpdateDataset } from "@/shared/domain/datasets/hooks/useUpdateDataset";
import { OrgRecord } from "@/shared/domain/orgs";

interface UpdateDatasetTagsProps {
  initialTags: string[];
  datasetId: string;
  currentOrg: OrgRecord | null;
  onTagsUpdated?: (record: DatasetRecord) => void;
}

/**
 * A dataset's tags in the shared {@link TagsEditor}, each change saved as a
 * metadata changeset. Shows the tags the server kept after each save.
 */
export const UpdateDatasetTags: React.FC<UpdateDatasetTagsProps> = ({
  initialTags,
  datasetId,
  currentOrg,
  onTagsUpdated,
}) => {
  const [tags, setTags] = React.useState<string[]>(initialTags);
  React.useEffect(() => setTags(initialTags), [initialTags]);

  const { mutateAsync: updateDataset } = useUpdateDataset();

  const save = async (metadata_changeset: {
    put_tags?: string[];
    remove_tags?: string[];
  }) => {
    const record = await updateDataset({
      datasetId,
      resourceOwnerId: currentOrg?.org_id || "",
      updates: { metadata_changeset },
    });
    setTags(record.tags);
    onTagsUpdated?.(record);
  };

  return (
    <TagsEditor
      tags={tags}
      recordKey={datasetId}
      autocompleteType={AutocompleteType.DatasetTags}
      onAdd={(tag) => save({ put_tags: [tag] })}
      onRemove={(tag) => save({ remove_tags: [tag] })}
      analytics={{
        add: "AddDatasetTagClicked",
        remove: "RemoveDatasetTagClicked",
        props: { datasetId },
      }}
    />
  );
};
